import { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FiltersQuery, MidnightQueries, Queries } from '@services';
import { getParams } from '@helpers';
import { useFilters } from './useFilters';
import _ from 'lodash';

export const DEFAULT_QUERIES: Queries = {
  page: 0,
  size: 10,
  sortBy: '',
  sortDirection: null,
  filterText: '',
  filterTextBy: '',
  platform: '',
  category: '',
  postType: '',
  caseState: '',
  complianceStatus: '',
};

export const DEFAULT_QUERIES_MIDNIGHT: MidnightQueries = {
  page: 1,
  pageSize: 10,
  sortBy: '',
  sortOrder: null,
  filterText: '',
  filterTextBy: '',
};

export const DEFAULT_QUERIES_ENTERPRISE = { ...DEFAULT_QUERIES, page: 1 };

const getFilters = (
  defaultQueries: Queries | MidnightQueries,
  params: { [k: string]: string | string[] },
): [string[], FiltersQuery] => {
  const queryKeys = Object.keys(defaultQueries);
  let selectedFilters = Object.keys(params).filter((key) => !queryKeys.includes(key));
  const filters = selectedFilters.reduce((acc, key) => ({ ...acc, [key]: params[key] }), {});
  if (selectedFilters.includes('updatedStartDate') || selectedFilters.includes('updatedEndDate')) {
    selectedFilters = [
      ...selectedFilters.filter((type) => type !== 'updatedStartDate' && type !== 'updatedEndDate'),
      'lastModified',
    ];
  }
  return [selectedFilters, filters];
};

export const useTableQueries = (
  preSelectedFilters?: { [key: string]: string | undefined | string[] },
  defaultQueries?: Partial<Queries>,
  isEnterprise?: boolean,
  usingMidnightResponse?: boolean,
) => {
  const { search } = useLocation();

  const defaultQueriesParams = {
    ...(isEnterprise
      ? usingMidnightResponse
        ? DEFAULT_QUERIES_MIDNIGHT
        : DEFAULT_QUERIES_ENTERPRISE
      : DEFAULT_QUERIES),
    ...defaultQueries,
  };

  const params = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    return {
      ...getParams(searchParams),
      filterText: searchParams.get('filterText') || '',
      filterTextBy: searchParams.getAll('filterTextBy'),
      platform: searchParams.getAll('platform'),
      category: searchParams.getAll('category'),
      postType: searchParams.getAll('postType'),
      caseState: searchParams.getAll('caseState'),
      complianceStatus: searchParams.getAll('complianceStatus'),
    };
  }, [search]);

  const [defaultSelectedFilters, defaultFilters] = getFilters(defaultQueriesParams, params);
  const [selectedFilters, setSelectedFilters] = useState<string[]>(defaultSelectedFilters);
  const { filterChange, resetFilter, filters, setFilters } = useFilters(defaultFilters);

  const [queries, setQueries] = useState<Queries | MidnightQueries>({
    ...defaultQueriesParams,
    ...params,
  });

  useEffect(() => {
    const newQueries = { ...queries, ...params };
    if (!_.isEqual(newQueries, queries)) {
      setQueries(newQueries);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setFilters((prev) => ({ ...prev, ...preSelectedFilters }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedFilters]);

  return {
    queries,
    setQueries,
    filterChange,
    resetFilter,
    filters,
    setFilters,
    selectedFilters,
    setSelectedFilters,
  };
};
